/**
 * Feedback API utilities for @landtrustinc/buck-chat
 * Handles message feedback submission (helpful/not helpful)
 */

import { handleNetworkError } from './apiClient';

/**
 * Feedback payload type
 */
export type FeedbackPayload = {
  messageId: string;
  isHelpful: boolean;
  sessionId?: string | null;
  timestamp: string;
  userId?: string;
  context?: {
    listingId?: string;
    packageId?: string;
  };
};

/**
 * Send feedback for a chat message
 * Fails silently on error since feedback is non-critical
 *
 * @param feedbackUrl - The URL to send feedback to
 * @param payload - The feedback payload
 * @param enableDebugLogs - Whether to log debug information
 */
export const sendFeedback = async (
  feedbackUrl: string,
  payload: FeedbackPayload,
  enableDebugLogs = false
): Promise<void> => {
  try {
    if (enableDebugLogs) {
      console.log(
        `👍 Sending feedback: ${payload.isHelpful ? 'helpful' : 'not helpful'} for message: ${payload.messageId}`,
        payload
      );
    }

    const response = await fetch(feedbackUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (enableDebugLogs) {
      console.log('✅ Feedback sent successfully');
    }
  } catch (error) {
    if (enableDebugLogs) {
      console.error('❌ Failed to send feedback:', error);
    }
    // Re-throw wrapped error for callers who want to handle it
    throw handleNetworkError(error);
  }
};
